import { navigate } from "gatsby-link"
import { useLayoutEffect, useState, useCallback } from "react"
import Cookies from "js-cookie"
import { useRecoilState, useRecoilValue } from "recoil"
import { checkLogin, isBrowser, logout } from "../services/auth"
import { LanguageState } from "../status/LanguageState"
import { userLoginState } from "../status/UserState"
import { clickLoginHandler } from "../utility/LoginHandler"

// ログイン状態の型定義
type LoginStatus = 'checking' | 'logged-in' | 'not-logged-in' | 'error' | 'cancelled'

// エラーハンドリング用の型定義
interface LoginError {
    code: string
    message: string
    shouldRetry: boolean
}

const useLogin = () => {
    const language = useRecoilValue(LanguageState)
    const [userLogin, setUserLogin] = useRecoilState(userLoginState)
    const [loginStatus, setLoginStatus] = useState<LoginStatus>('checking')

    // エラーハンドリング関数
    const handleLoginError = useCallback((error: any): LoginError => {
        const errorString = error.toString()
        
        if (errorString.includes('Error: 999') || errorString.includes('Error: -1') || errorString.includes('Error: -2')) {
            return {
                code: 'LOOP_ERROR',
                message: 'ループエラーが発生しました',
                shouldRetry: false
            }
        }
        
        if (errorString.includes('Error: 110') || errorString.includes('Error: 120') || errorString.includes('Error: 121')) {
            return {
                code: 'API_ERROR',
                message: 'APIエラーが発生しました',
                shouldRetry: false
            }
        }
        
        // Error: 122は会員期限切れエラーのため再試行しない
        if (errorString.includes('Error: 122')) {
            return {
                code: 'MEMBERSHIP_EXPIRED',
                message: '会員期限が切れています',
                shouldRetry: false
            }
        }
        
        return {
            code: 'UNKNOWN_ERROR',
            message: '不明なエラーが発生しました',
            shouldRetry: true
        }
    }, [])

    // ログイン成功時の処理
    const handleLoginSuccess = useCallback(() => {
        console.log("useLogin=>", location.pathname)
        
        // ログイン成功後のリダイレクト処理
        if (location.pathname.match(/\/connect/g)) {
            const redirectUrl = Cookies.get("connect_redirect")
            
            if (redirectUrl) {
                // 保存されたURLがあればそこにリダイレクト
                Cookies.remove("connect_redirect") // 使用後は削除
                navigate(redirectUrl)
            } else {
                // 保存されたURLがなければ/member/にリダイレクト
                const memberPath = `${language === "en" ? "/en" : ""}/member/`
                navigate(memberPath)
            }
        }
        
        setLoginStatus('logged-in')
        setUserLogin(true)
    }, [language])

    // ログインキャンセル時の処理
    const handleLoginCancel = useCallback(() => {
        setLoginStatus('cancelled')
    }, [])

    // ログインエラー時の処理
    const handleLoginErrorCallback = useCallback((error: any) => {
        console.log("API error", error)
        setLoginStatus('error')
        
        const errorInfo = handleLoginError(error)
        
        if (isBrowser()) {
            if (errorInfo.shouldRetry) {
                clickLoginHandler()
            } else {
                console.log("API error", errorInfo.message)
                // 500ページにリダイレクト
                const errorPath = `${language === "en" ? "/en" : ""}/500/`
                navigate(errorPath)
            }
        }
        
        logout({ callback: () => {} })
    }, [handleLoginError, language, navigate])

    useLayoutEffect(() => {
        // エラーページの場合は早期リターン
        if (location.pathname === "/error/" ||
            location.pathname === "/404/" ||
            location.pathname === "/500/") {
            setLoginStatus('not-logged-in')
            return
        }

        // connect または member ページでのみログインチェックを実行
        if (location.pathname.match(/\/connect/g) || location.pathname.match(/\/member/g)) {
            checkLogin({
                successCallback: handleLoginSuccess,
                cancelCallback: handleLoginCancel,
                errorCallback: handleLoginErrorCallback
            })
        } else {
            setLoginStatus('not-logged-in')
        }
    }, [handleLoginSuccess, handleLoginCancel, handleLoginErrorCallback])

    return {
        loginStatus,
        userLogin,
        isLoggedIn: loginStatus === 'logged-in',
        isLoading: loginStatus === 'checking'
    }
}

export { useLogin }